import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { apiErrorCode } from './api-error';

// Every anonymous /auth/* endpoint (login, login-method, forgot/reset-password): attaching a stale/foreign
// Bearer token is pointless, and running the 401 handler below on a wrong-password/expired-reset-token
// attempt would log out and redirect away from a session that might still be valid in another tab.
const ANONYMOUS_AUTH_PATHS = ['/auth/login', '/auth/login-method', '/auth/forgot-password', '/auth/reset-password'];
const isLoginRequest = (url: string) => ANONYMOUS_AUTH_PATHS.some((path) => url.endsWith(path));

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (isLoginRequest(req.url)) {
    return next(req);
  }

  const token = auth.accessToken;
  const authorizedReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authorizedReq).pipe(
    catchError((error) => {
      if (error.status === 401) {
        auth.logout();
        router.navigate(['/login']);
      }
      if (error.status === 403 && apiErrorCode(error) === 'ORGANIZATION_SUSPENDED') {
        // Behavior only (never text): the shell shows its own suspended notice from this flag.
        auth.markOrganizationSuspended();
      }
      return throwError(() => error);
    })
  );
};
